/* import __COLOCATED_TEMPLATE__ from './side-sheet.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { get } from 'embercom/lib/ajax';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'embercom/services/intl';
import type NotificationsService from 'embercom/services/notifications-service';

export interface Snapshot {
  id: number;
  created_at: string;
  created_by_admin_id: number;
  created_by_admin_name: string | null;
  is_current: boolean;
  is_draft: boolean;
  rollback_from_id: number | null;
  version_note: string | null;
}

interface Signature {
  Args: {
    editorState: EditorState;
    workflow: Workflow;
    isViewOnly?: boolean;
    onPreviewSnapshot?: (workflow: Workflow) => void;
    onRestoreWorkflow?: () => void;
  };
}

export default class VersionHistorySideSheet extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;

  @tracked snapshots: Snapshot[] = [];
  @tracked isError = false;
  @tracked selectedSnapshotId: number | null = null;
  @tracked isLoadingPreview = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadSnapshots).perform();
  }

  get isLoading() {
    return taskFor(this.loadSnapshots).isRunning;
  }

  get hasSnapshots() {
    return this.snapshots.length > 0;
  }

  get hasDraft() {
    return this.snapshots.some((snapshot) => snapshot.is_draft);
  }

  get workflowInstanceId() {
    return this.args.workflow.workflowInstanceId;
  }

  @action
  async onSnapshotClick(snapshot: Snapshot) {
    if (snapshot.id === this.selectedSnapshotId) {
      return;
    }

    this.selectedSnapshotId = snapshot.id;

    if (snapshot.is_current) {
      this.args.onRestoreWorkflow?.();
      return;
    }

    this.isLoadingPreview = true;
    try {
      let visualBuilderData: any = await get('/ember/operator_workflows/snapshot_preview', {
        app_id: this.appService.app.id,
        workflow_instance_id: this.workflowInstanceId,
        snapshot_id: snapshot.id,
      });

      let normalizedPayload = this.store.normalize('operator/visual-builder/workflow', {
        ...visualBuilderData,
        id: `snapshot-preview-${snapshot.id}`,
      });
      let previewWorkflow = this.store.push(normalizedPayload) as unknown as Workflow;
      this.args.onPreviewSnapshot?.(previewWorkflow);
    } catch (e: unknown) {
      this.selectedSnapshotId = null;
      this.notificationsService.notifyError(this.intl.t('workflows.version-history.load-error'));
    } finally {
      this.isLoadingPreview = false;
    }
  }

  @task({ drop: true })
  *loadSnapshots(): TaskGenerator<void> {
    this.isError = false;
    try {
      let snapshots: Snapshot[] = yield get('/ember/operator_workflows/snapshot_history', {
        app_id: this.appService.app.id,
        workflow_instance_id: this.workflowInstanceId,
      });
      this.snapshots = snapshots ?? [];
    } catch (e: unknown) {
      this.isError = true;
      this.notificationsService.notifyError(this.intl.t('workflows.version-history.load-error'));
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::VersionHistory::SideSheet': typeof VersionHistorySideSheet;
    'workflows/version-history/side-sheet': typeof VersionHistorySideSheet;
  }
}
